import {
  TrendingUp,
  DollarSign,
  ClipboardCheck,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { formatCurrency } from "../../../owner/data/ownerPrototype";
import type { BaselineData, Identifier } from "../../../pages/ProjectManagement/types";
import { isInternalManproProject } from "../isInternalManproProject";
import InternalWorkspaceBanner from "./InternalWorkspaceBanner";

type ProjectWorkspaceOverviewProps = {
  baselineData: BaselineData;
  activeProjectId: Identifier | null;
  activeProjectData: any | null; // ProjectTree
  workspaceTitle: string;
  workspaceStatus: string;
  nextAction: string;
  progressAverage: number;
  milestoneCount: number;
  taskCount: number;
  isRejectedProject: boolean;
  isPendingApproval: boolean;
  isProjectLoading?: boolean;
  onClose: () => void;
  onShareLink: () => void;
};

const ProjectWorkspaceOverview = ({
  baselineData,
  activeProjectId,
  activeProjectData,
  workspaceTitle,
  workspaceStatus,
  nextAction,
  progressAverage,
  milestoneCount,
  taskCount,
  isRejectedProject,
  isPendingApproval,
  isProjectLoading,
  onClose,
  onShareLink,
}: ProjectWorkspaceOverviewProps) => {

  const lastRejectedApproval = (activeProjectData as any)?.approvals
    ?.filter((a: any) => a.status === "rejected")
    ?.sort((a: any, b: any) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())[0];

  return (
    <div className="mb-6 space-y-4 sm:space-y-5">
      {/* Back Button */}
      <button
        type="button"
        onClick={onClose}
        className="inline-flex items-center gap-2 text-xs sm:text-sm font-extrabold text-blue-700 transition hover:text-blue-800 active:scale-95"
      >
        <span aria-hidden="true">←</span> Kembali ke Kelola Proyek
      </button>

      {isInternalManproProject(activeProjectData) && <InternalWorkspaceBanner />}

      {/* BANNER: RAB/Baseline ditolak Owner */}
      {isRejectedProject && (
        <section className="rounded-2xl sm:rounded-3xl border border-red-200 bg-gradient-to-r from-red-50 to-orange-50 p-4 sm:p-5 shadow-sm">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="grid h-9 w-9 sm:h-10 sm:w-10 shrink-0 place-items-center rounded-full bg-red-100 text-red-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                className="sm:h-5 sm:w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] sm:text-xs font-extrabold uppercase tracking-wider text-red-600">
                RAB / Baseline Ditolak
              </p>
              <h3 className="mt-1 text-sm sm:text-base font-extrabold text-red-900 leading-snug">
                Rencana Anggaran Biaya Anda ditolak oleh Owner
              </h3>
              <p className="mt-1 text-xs sm:text-sm leading-relaxed text-red-700">
                Silakan buka tab <strong>Planning</strong>, revisi RAB/Baseline sesuai catatan Owner, lalu kirim ulang untuk persetujuan.
              </p>
              {lastRejectedApproval?.rejection_reason && (
                <div className="mt-3 rounded-xl border border-red-200 bg-white/90 p-3 shadow-xs">
                  <p className="text-[11px] sm:text-xs font-extrabold text-red-600">Alasan Penolakan:</p>
                  <p className="mt-1 text-xs sm:text-sm font-semibold text-slate-700 leading-relaxed break-words">
                    {lastRejectedApproval.rejection_reason}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Project Title Card */}
      <div className="rounded-2xl sm:rounded-3xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <span
              className={`inline-block rounded-full px-2.5 py-0.5 sm:px-3 sm:py-1 text-xs font-extrabold ${isRejectedProject
                  ? "bg-red-50 text-red-700"
                  : isPendingApproval
                    ? "bg-amber-50 text-amber-700"
                    : "bg-blue-50 text-blue-700"
                }`}
            >
              {workspaceStatus}
            </span>

            <h1 className="mt-2.5 sm:mt-3 text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight text-slate-900 break-words">
              {workspaceTitle}
            </h1>

            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3">
              <p className="text-xs sm:text-sm leading-relaxed text-slate-500">
                {nextAction}
              </p>
              {activeProjectId && (
                <button
                  type="button"
                  onClick={onShareLink}
                  className="inline-flex w-fit shrink-0 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 sm:px-3 text-xs font-bold text-blue-700 transition hover:bg-blue-100 hover:border-blue-300 active:scale-95"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="13"
                    className="sm:h-3.5 sm:w-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  <span>Share Guest Link</span>
                </button>
              )}
            </div>
          </div>

          {/* Next Action Box */}
          <div className="w-full lg:w-72 shrink-0 rounded-xl sm:rounded-2xl border border-blue-100 bg-blue-50/80 p-3.5 sm:p-4">
            <p className="text-[11px] sm:text-xs font-extrabold uppercase tracking-wider text-blue-700">
              Next Action
            </p>
            <p className="mt-1 sm:mt-2 text-xs sm:text-sm font-semibold leading-relaxed text-slate-700 break-words">
              {nextAction}
            </p>
          </div>
        </div>

        {/* 4 Stat Cards */}
        <div className="mt-5 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {(
            [
              ["Status", workspaceStatus, ShieldCheck, "bg-blue-50 text-blue-700"],
              ["Progress", isProjectLoading ? "-" : `${progressAverage}%`, TrendingUp, "bg-emerald-50 text-emerald-700"],
              ["Budget", isProjectLoading ? "Memuat..." : formatCurrency(Number(activeProjectData?.budget || baselineData.budget || 0)), DollarSign, "bg-amber-50 text-amber-700"],
              ["Milestone / Task", isProjectLoading ? "-" : `${milestoneCount} / ${taskCount}`, ClipboardCheck, "bg-purple-50 text-purple-700"],
            ] satisfies Array<[string, string | number, LucideIcon, string]>
          ).map(([label, value, Icon, tone]) => (
            <div
              key={String(label)}
              className="min-w-0 rounded-xl sm:rounded-2xl border border-slate-200 bg-white p-3.5 sm:p-4 transition hover:border-slate-300"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] sm:text-xs font-extrabold uppercase tracking-wide text-slate-500 truncate">
                    {String(label)}
                  </p>
                  <p className="mt-1 sm:mt-2 truncate text-base sm:text-lg font-extrabold text-slate-900">
                    {String(value)}
                  </p>
                </div>
                <div className={`grid h-8 w-8 sm:h-9 sm:w-9 shrink-0 place-items-center rounded-lg sm:rounded-xl ${String(tone)}`}>
                  <Icon size={18} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectWorkspaceOverview;
